import { useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'

import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'

import {
    getItemColumns,
    getItemFilterItems,
    getItemFilterToggles,
} from '../components/Items/item-list-config'
import { getItems } from '../services'

interface ItemListFilters {
    item_type: string
    status: string
    condition: string
    owner: string
    location_id: string
    rack_id: string
    supplier_id: string
    model_id: string
}

const FILTER_DEFAULTS: ItemListFilters = {
    item_type: '',
    status: '',
    condition: '',
    owner: '',
    location_id: '',
    rack_id: '',
    supplier_id: '',
    model_id: '',
}

// Map tab values to actual DB category values (supports grouped categories)
export const CATEGORY_MAP: Record<string, string> = {
    disk: 'sas,ssd,nvme,hdd',
}

export const resolveCategory = (tab: string): string => CATEGORY_MAP[tab] || tab

export const useItemList = (category: string = 'server') => {
    // URL params & form
    const {
        params,
        filters,
        queryParams,
        setParams,
        setFilter,
        resetParams,
        searchInputProps,
    } = useUrlSearchParams<ItemListFilters>({
        defaults: { sort_by: 'created_at', sort_order: 'desc' },
        filterDefaults: FILTER_DEFAULTS,
    })

    const { control, reset: resetForm } = useForm<ItemListFilters>({
        defaultValues: filters,
    })

    // Reset both URL params and form state
    const resetAll = useCallback(() => {
        resetParams()
        resetForm(FILTER_DEFAULTS)
    }, [resetParams, resetForm])

    // Data fetching — inject resolved category from tabs
    const resolvedCategory = resolveCategory(category)
    const fetchFn = useCallback(
        () => getItems({ ...queryParams, category: resolvedCategory }),
        [queryParams, resolvedCategory]
    )

    const { data, refetch, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.WAREHOUSE_ITEMS, category, queryParams],
        queryFn: fetchFn,
        placeholderData: true,
    })

    // Memoized configs
    const columns = useMemo(() => getItemColumns(), [])
    const filterItems = useMemo(
        () => getItemFilterItems(filters.location_id, resolvedCategory),
        [filters.location_id, resolvedCategory]
    )
    const filterToggles = useMemo(() => getItemFilterToggles(), [])

    // Handlers
    const handleSortChange = useCallback(
        (sortBy: string, sortOrder: 'asc' | 'desc') =>
            setParams({ sort_by: sortBy, sort_order: sortOrder }),
        [setParams]
    )
    const handlePageChange = useCallback(
        (page: number) => setParams({ page }, false),
        [setParams]
    )
    const handlePageSizeChange = useCallback(
        (pageSize: number) => setParams({ page_size: pageSize, page: 1 }),
        [setParams]
    )

    return {
        items: data?.results || [],
        pagination: data?.pagination,
        stateComponent: renderState(),

        // Exposed for CSV export (full filtered set, not just current page)
        queryParams,
        resolvedCategory,

        control,
        setFilter,
        params,
        searchInputProps,

        columns,
        filterItems,
        filterToggles,

        handleSortChange,
        handlePageChange,
        handlePageSizeChange,
        resetAll,
        refetch,
    }
}
